/* eslint-disable complexity */
/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { FacetNavigationEntry } from '@/components/content/FacetNavigation/parts/Entry';
import { FacetNavigationPrice } from '@/components/content/FacetNavigation/parts/Price';
import { facetNavigationAccordionSX } from '@/components/content/FacetNavigation/styles/accordion';
import { facetNavigationAccordionSummarySX } from '@/components/content/FacetNavigation/styles/accordionSummary';
import { useFacetNavigation } from '@/data/Content/FacetNavigation';
import { useLocalization } from '@/data/Localization';
import { FACET_PRICE_PREFIX } from '@/data/constants/facet';
import { ContentContext } from '@/data/context/content';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Breakpoint,
	Button,
	Grid,
	IconButton,
	Stack,
	Typography,
	useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { isEmpty } from 'lodash';
import React, { FC, SyntheticEvent, useCallback, useContext, useMemo, useState } from 'react';

const mobileBreakpoint: Breakpoint = 'md';

export const FacetNavigationItemChange: FC = () => {
	const productFilterNLS = useLocalization('ProductFilter');
	const { toggleFacetLimit, facets, selectedFacet, filteredParams } = useContext(
		ContentContext
	) as ReturnType<typeof useFacetNavigation>;
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down(mobileBreakpoint));
	const [activeFacetId, setActiveFacetId] = useState<string>(() => '');
	const handleAccordionChange = useCallback(
		(facetValue: string) => (_event: SyntheticEvent<Element, Event>, isExpanded: boolean) => {
			isMobile && setActiveFacetId(isExpanded ? facetValue : '');
		},
		[isMobile]
	);

	// Create nested category facet structure, which may not exist
	const nestedCategoryFacet = useMemo(() => {
		const categoryFacet = facets.find((facet) => facet.name === 'ParentCatalogGroup');
		const filteredCategoryFacet: any = categoryFacet?.entry?.filter(
			(item: { label: string }) => item?.label?.toLowerCase() !== 'all products'
		);
		// const nestedArray = createNestedStructure(filteredItem);
		const tempObj: { [key: string]: DataItem } = {};
		const outPutObj: { [key: string]: DataItem } = {};

		// Initialize tempObj with all items and an empty children array
		(filteredCategoryFacet as DataItem[])?.forEach((obj) => {
			tempObj[obj.extendedData.uniqueId] = { ...obj, children: [] };
		});

		// Find top-level items and add to outPutObj
		(filteredCategoryFacet as DataItem[])?.forEach((obj) => {
			const parentIds = obj.extendedData.parentIds;
			if (!parentIds || (!parentIds.includes('_') && !tempObj[parentIds])) {
				outPutObj[obj.extendedData.uniqueId] = tempObj[obj.extendedData.uniqueId];
			}
		});

		// Build the nested structure
		(filteredCategoryFacet as DataItem[])?.forEach((obj) => {
			const { uniqueId, parentIds } = obj?.extendedData;
			if (parentIds) {
				const parentIdArray = parentIds?.split('_');
				const parentId = parentIdArray[parentIdArray.length - 1];

				if (tempObj[parentId]) {
					tempObj[parentId]?.children?.push(tempObj[uniqueId]);
				}
			}
		});

		// Convert outPutObj to an array and filter out items with the label 'hidden'
		const OutPutData = Object.keys(outPutObj).map((key) => tempObj[key]);
		const filteredCatData = OutPutData?.filter((item) => item?.label?.toLowerCase() !== 'hidden');

		return filteredCatData;
	}, [facets]);
	const { minPrice = -1, maxPrice = -1 } = filteredParams;
	const isExpandedOnMobile = (facetItem: any) => {
		console.log('activeFacetId', activeFacetId);
		if (facetItem?.value?.startsWith(FACET_PRICE_PREFIX) && minPrice > -1 && maxPrice > -1) {
			return true;
		}
		const shouldExpand = facetItem?.entry?.some((item: any) => selectedFacet.includes(item.value));
		if (shouldExpand) {
			return true;
		} else {
			return false;
		}
	};

	return (
		<>
			{facets.map((facetItem) => (
				<>
					{facetItem.name === 'ParentCatalogGroup' && !isEmpty(nestedCategoryFacet) ? (
						<FacetCategoryNavigation
							facetItem={facetItem}
							nestedCategoryFacet={nestedCategoryFacet}
							selectedFacet={selectedFacet}
						/>
					) : (
						<Accordion
							id={`productFilter-facet-${facetItem.name}`}
							data-testid={`productFilter-facet-${facetItem.name}`}
							defaultExpanded={!isMobile ? true : isExpandedOnMobile(facetItem)}
							key={facetItem.value}
							{...(!isMobile ? { expanded: true } : {})}
							onChange={handleAccordionChange(facetItem.value)}
							square={true}
							sx={facetNavigationAccordionSX}
						>
							<AccordionSummary
								aria-controls={`${facetItem.value}-content`}
								id={`${facetItem.value}-header`}
								data-testid={`${facetItem.value}-header`}
								sx={facetNavigationAccordionSummarySX}
								expandIcon={isMobile ? <ExpandMoreIcon /> : null}
							>
								<Typography
									variant="body2"
									sx={{
										paddingTop: { md: '16px' },
										typography: { md: 'body1' },
										fontWeight: { md: 'bold' },
									}}
								>
									{facetItem.value.startsWith(FACET_PRICE_PREFIX)
										? productFilterNLS.Labels.price.t()
										: facetItem.extendedData?.fname ?? facetItem.name}
								</Typography>
							</AccordionSummary>
							<AccordionDetails>
								{facetItem.value.startsWith(FACET_PRICE_PREFIX) ? (
									<FacetNavigationPrice />
								) : (
									<Stack justifyContent="flex-start" alignItems="flex-start">
										<Grid container>
											{facetItem.entry?.map((entry: any) => (
												<Grid item key={entry.value}>
													<FacetNavigationEntry entry={entry} />
												</Grid>
											))}
										</Grid>
										{facetItem.entry && (facetItem.showMore || facetItem.showLess) ? (
											<Button
												variant="inline"
												data-testid={`productFilter_${facetItem.name}_show_more`}
												id={`productFilter_${facetItem.name}_show_more`}
												onClick={toggleFacetLimit(facetItem.value)}
												sx={{
													color: 'link.main',
													textDecoration: 'none',
													fontWeight: '500',
													':hover': {
														color: 'link.main',
														textDecoration: 'underline',
													},
												}}
											>
												{facetItem.showMore
													? productFilterNLS.Actions.showMore.t()
													: productFilterNLS.Actions.showLess.t()}
											</Button>
										) : null}
									</Stack>
								)}
							</AccordionDetails>
						</Accordion>
					)}
				</>
			))}
		</>
	);
};

type ExtendedData = {
	uniqueId: string;
	parentIds?: string;
};

type DataItem = {
	label: string;
	value: string;
	count: string;
	extendedData: ExtendedData;
	children: DataItem[];
};

const FacetCategoryNavigation: FC<{
	facetItem: any;
	nestedCategoryFacet: DataItem[];
	selectedFacet: any;
}> = ({ facetItem, nestedCategoryFacet, selectedFacet }) => {
	const productFilterNLS = useLocalization('ProductFilter');
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down(mobileBreakpoint));
	const [activeFacetId, setActiveFacetId] = useState<string>(() => '');
	const handleAccordionChange = useCallback(
		(facetValue: string) => (_event: SyntheticEvent<Element, Event>, isExpanded: boolean) => {
			isMobile && setActiveFacetId(isExpanded ? facetValue : '');
		},
		[isMobile]
	);

	const isExpandedOnMobile = (facetItem: any) => {
		console.log('activeFacetId', activeFacetId);
		const shouldExpand = facetItem?.entry?.some((item: any) => selectedFacet.includes(item.value));
		if (shouldExpand) {
			return true;
		} else {
			return false;
		}
	};

	return (
		<Accordion
			id={`productFilter-facet-${facetItem.name}`}
			data-testid={`productFilter-facet-${facetItem.name}`}
			defaultExpanded={!isMobile ? true : isExpandedOnMobile(facetItem)}
			key={facetItem.value}
			{...(!isMobile ? { expanded: true } : {})}
			onChange={handleAccordionChange(facetItem.value)}
			square={true}
			sx={facetNavigationAccordionSX}
		>
			<AccordionSummary
				aria-controls={`${facetItem.value}-content`}
				id={`${facetItem.value}-header`}
				data-testid={`${facetItem.value}-header`}
				sx={facetNavigationAccordionSummarySX}
				expandIcon={isMobile ? <ExpandMoreIcon /> : null}
			>
				<Typography
					variant="body2"
					sx={{
						paddingTop: { md: '16px' },
						typography: { md: 'body1' },
						fontWeight: { md: 'bold' },
					}}
				>
					{facetItem.value.startsWith(FACET_PRICE_PREFIX)
						? productFilterNLS.Labels.price.t()
						: facetItem.extendedData?.fname ?? facetItem.name}
				</Typography>
			</AccordionSummary>
			<AccordionDetails>
				<FacetCategoryNavigatioToggle nestedArray={nestedCategoryFacet} />
			</AccordionDetails>
		</Accordion>
	);
};

interface FacetCategoryNavigatioProps {
	nestedArray: Category[];
}

interface Category {
	value: string;
	label: string;
	children: Category[];
}

const FacetCategoryNavigatioToggle: FC<FacetCategoryNavigatioProps> = ({ nestedArray }) => {
	const [expandedState, setExpandedState] = useState<{ [key: string]: boolean }>({
		'0': true,
		'0-0': true,
	});

	const toggleExpand = (key: string) => {
		setExpandedState((prevState) => ({
			...prevState,
			[key]: !prevState[key],
		}));
	};

	const renderChildren = (children: Category[], parentKey: string, paddingLeft: number) =>
		children?.map((child: any, childIndex) => {
			const childKey = `${parentKey}-${childIndex}`;
			return (
				<React.Fragment key={childKey}>
					<Grid item sx={{ paddingLeft: `${paddingLeft}px`, display: 'flex', flex: '0 0 100%' }}>
						<FacetNavigationEntry entry={child} />
						{child?.children?.length > 0 ? (
							<IconButton sx={{ padding: 0 }} onClick={() => toggleExpand(childKey)}>
								{expandedState[childKey] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
							</IconButton>
						) : null}
					</Grid>
					{expandedState?.[childKey] && child?.children?.length > 0 ? (
						<Grid>{renderChildren(child?.children, childKey, paddingLeft + 15)}</Grid>
					) : null}
				</React.Fragment>
			);
		});

	return (
		<Stack justifyContent="flex-start" alignItems="flex-start">
			<Grid container>
				{nestedArray?.map((entry: any, entryIndex) => (
					<>
						<Grid item sx={{ display: 'flex', flex: '0 0 100%' }}>
							<FacetNavigationEntry entry={entry} />
							{entry?.children?.length > 0 ? (
								<IconButton
									sx={{ padding: 0 }}
									onClick={() => toggleExpand(entryIndex?.toString())}
								>
									{expandedState[entryIndex?.toString()] ? (
										<KeyboardArrowUpIcon />
									) : (
										<KeyboardArrowDownIcon />
									)}
								</IconButton>
							) : null}
						</Grid>
						{expandedState[entryIndex?.toString()] && entry?.children?.length > 0 ? (
							<>{renderChildren(entry?.children, entryIndex.toString(), 15)}</>
						) : null}
					</>
				))}
			</Grid>
		</Stack>
	);
};
